import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import {
  LayoutDashboard,
  Users,
  Facebook,
  Plug,
  Zap,
  Settings,
  Webhook,
  Sun,
  Moon,
  LogOut,
  Menu,
  X,
  ChevronDown,
  FileText,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLocale, LOCALES } from '../contexts/LocaleContext';
import { trpc } from '../lib/trpc';
import { Button } from './ui';

interface NavItem {
  label: string;
  href?: string;
  icon: ReactNode;
  children?: NavItem[];
  match?: string[];
}

function useNav(): { main: NavItem[] } {
  const { t } = useLocale();
  const main: NavItem[] = [
    { label: t('nav.overview'), href: '/overview', icon: <LayoutDashboard className="h-4 w-4" /> },
    { label: t('nav.leads'), href: '/leads', icon: <Users className="h-4 w-4" />, match: ['/leads'] },
    {
      label: t('nav.facebook'),
      icon: <Facebook className="h-4 w-4" />,
      children: [
        { label: t('nav.connections'), href: '/connections', icon: <Plug className="h-4 w-4" /> },
        { label: t('nav.forms'), href: '/forms', icon: <FileText className="h-4 w-4" /> },
      ],
    },
    { label: t('nav.webhookHealth'), href: '/webhook', icon: <Webhook className="h-4 w-4" /> },
    {
      label: t('nav.settings'),
      href: '/settings',
      icon: <Settings className="h-4 w-4" />,
      match: ['/settings'],
    },
  ];
  return { main };
}

function NavLink({ item, depth = 0 }: { item: NavItem; depth?: number }) {
  const [location] = useLocation();
  const [open, setOpen] = useState(true);
  const active = item.href
    ? location === item.href || (item.match?.some((m) => location.startsWith(m)) ?? false)
    : false;
  if (item.children) {
    return (
      <div>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
        >
          <span className="flex items-center gap-2">
            {item.icon}
            {item.label}
          </span>
          <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
        </button>
        {open && (
          <div className="ml-3 mt-0.5 space-y-0.5 border-l pl-2">
            {item.children.map((c) => (
              <NavLink key={c.label} item={c} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }
  return (
    <Link
      href={item.href!}
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      )}
    >
      {item.icon}
      {item.label}
    </Link>
  );
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { main } = useNav();
  return (
    <div className="flex h-full flex-col" onClick={onNavigate}>
      <div className="flex h-14 items-center gap-2 border-b px-5">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary text-primary-foreground">
          <Zap className="h-4 w-4" />
        </div>
        <span className="text-lg font-semibold">LeadHub</span>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
        {main.map((i) => (
          <NavLink key={i.label} item={i} />
        ))}
      </nav>
    </div>
  );
}

export function DashboardLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { locale, setLocale, t } = useLocale();
  const attention = trpc.connections.attentionCount.useQuery(undefined, { enabled: !!user });
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r bg-card md:block">
        <Sidebar />
      </aside>
      {/* mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-card shadow-xl">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b bg-card/80 px-4 backdrop-blur md:px-6">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {!!attention.data && attention.data > 0 && (
              <Link
                href="/connections"
                className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-600 dark:text-amber-400"
              >
                {attention.data === 1
                  ? t('layout.connectionsNeedAttentionOne', { count: attention.data })
                  : t('layout.connectionsNeedAttention', { count: attention.data })}
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={locale}
              onChange={(e) => setLocale(e.target.value as typeof locale)}
              className="h-8 rounded-md border bg-background px-2 text-xs"
            >
              {LOCALES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.flag} {l.label}
                </option>
              ))}
            </select>
            <Button variant="ghost" size="icon" onClick={toggle} title={t('layout.toggleTheme')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight">{user?.name}</p>
              <p className="text-xs leading-tight text-muted-foreground">{user?.email}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => void logout()}>
              <LogOut className="h-3.5 w-3.5" /> {t('layout.signOut')}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="mx-auto max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
